/* eslint-disable complexity */
/* eslint-disable max-lines-per-function */
import type { Options } from 'highcharts'
import dynamic from 'next/dynamic'
import { useEffect, useState } from 'react'
import classNames from 'classnames'
import getIndexCalculatorChartOptions from './IndexCalculatorChartOptions'
import IndexETFComposition from './IndexETFComposition'
import { Benchmark, Peer } from './types'
import TimePeriodSeries from '@/stories/components/Common/Charts/TimePeriodSeries'
import { LAKH_UNIT, RUPEE_SYMBOL, THOUSAND_UNIT } from '@/root/constants'
import RangeSlider from '@/stories/components/Common/FormComponents/RangeSlider'
import { yieldToMain } from '@/root/utils/yield-to-main'

const ChartLoader = () => (
  <div className="mb-4 mt-10 h-[250px] animate-pulse rounded bg-gray-100" />
)
const DynamicChartWithNoSSR = dynamic(
  () => import('@/stories/components/Common/Charts/ChartsMore'),
  {
    ssr: false,
    loading: ChartLoader,
  },
)

function getYearMultiplierFromLabel(label?: string) {
  const lastChar = label?.[label.length - 1]

  if (label && lastChar === 'Y') {
    return Number(label.slice(0, label.length - 1))
  } else return 1
}

export default function IndexCalculator({
  benchmarks,
  etfs_composition,
  name,
  mode = 'index',
  peer_rank,
  total_peers,
  installments,
  containerClassName = '',
  childClass = '',
  headingClassName = '',
}: {
  benchmarks?: Benchmark[]
  etfs_composition?: Peer[]
  name?: string
  mode?: string
  peer_rank?: number
  total_peers?: number
  installments?: number
  containerClassName?: string
  childClass?: string
  headingClassName?: string
}) {
  const [state, setState] = useState<{
    amount: number
    duration: string
    chartOptions?: Options
  }>({
    amount: 1,
    duration: benchmarks?.map(item => item.label)[0] ?? '3M',
  })

  const units = mode === 'sip' ? THOUSAND_UNIT : LAKH_UNIT

  const etfComposition =
    etfs_composition?.find(item => item.label === state.duration)
      ?.composition ??
    etfs_composition?.find(item => item.label === state.duration)?.comparison

  const currValue =
    mode === 'sip'
      ? benchmarks?.find(item => item.label === state.duration)?.comparison?.[0]
          ?.sip_multiplier ?? 0
      : benchmarks?.find(item => item.label === state.duration)?.comparison?.[0]
          ?.current_value ?? 0

  const yearMultiplier = getYearMultiplierFromLabel(state.duration)

  const absoluteReturns = state.amount * currValue

  const sipInvestedAmount = (installments ?? 1) * yearMultiplier * state.amount

  const returnsVal =
    mode === 'sip' ? absoluteReturns - sipInvestedAmount : absoluteReturns

  const returnsPercentage =
    mode === 'sip'
      ? (returnsVal / sipInvestedAmount) * 100
      : (currValue - 1) * 100

  useEffect(() => {
    if (benchmarks && state.duration && state.amount) {
      setState(prevState => {
        return {
          ...prevState,
          chartOptions: getIndexCalculatorChartOptions(
            benchmarks,
            state.duration,
            state.amount,
            units,
            mode,
            sipInvestedAmount,
          ),
        }
      })
    }
  }, [
    state.amount,
    state.duration,
    benchmarks,
    units,
    mode,
    absoluteReturns,
    sipInvestedAmount,
    installments,
  ])

  const onChangeAmount = (investedAmount: string) => {
    setState(prevState => {
      return { ...prevState, amount: Number(investedAmount) }
    })
  }

  const onTimePeriodSelected = (month: string) => {
    setState(prevState => {
      return {
        ...prevState,
        duration: month,
      }
    })
  }

  return (
    <div
      className={classNames(
        'bg-brand-white mt-5 rounded-lg',
        containerClassName,
      )}
    >
      <div
        className={classNames(
          'flex flex-col  items-center rounded-lg bg-white p-6',
          {
            'p-3': mode !== 'etf',
          },
          childClass,
        )}
      >
        {name && mode !== 'sip' && (
          <div
            className={classNames(
              'mb-3 self-start font-medium',
              {
                'text-base': mode !== 'etf',
                'text-lg': mode === 'etf',
              },
              headingClassName,
            )}
          >
            What if you invested in {name}
          </div>
        )}
        {mode === 'sip' && (
          <div className={classNames('mb-3 self-start text-base font-medium')}>
            Past Returns SIP Calculator
          </div>
        )}
        <div className="w-full">
          <RangeSlider
            name="amount"
            min={1}
            max={10}
            step={0.1}
            units={units}
            valuePrefix={RUPEE_SYMBOL}
            defaultValue={state.amount}
            onChangeRange={onChangeAmount}
          />
        </div>
        <div className="mt-4 flex w-full flex-col">
          <div className="flex justify-between">
            {name && mode !== 'sip' && (
              <div className="w-2/3 text-base font-medium">
                {
                  benchmarks?.find(item => item.label === state.duration)
                    ?.comparison?.[0].name
                }{' '}
                would be worth
              </div>
            )}
            {mode === 'sip' && (
              <div className="w-2/3 text-base font-medium">
                Past returns from this stock
              </div>
            )}
            {typeof currValue === 'number' && (
              <div
                className={classNames({
                  'text-brand-red-cinnabar':
                    (mode === 'sip' && returnsVal < 0) ||
                    (mode !== 'sip' && currValue < 1),
                  'text-brand-green-dark':
                    (mode === 'sip' && returnsVal >= 0) ||
                    (mode !== 'sip' && currValue >= 1),
                })}
              >
                <span>
                  {RUPEE_SYMBOL}
                  {returnsVal.toFixed(2)}
                  {units}
                </span>
              </div>
            )}
          </div>
          {typeof currValue === 'number' && (
            <div className="mb-3 flex justify-between">
              <span className="w-2/3 text-sm">
                With absolute return in the last {state.duration}
              </span>

              <span
                className={classNames({
                  'text-brand-red-cinnabar text-xs':
                    (mode === 'sip' && returnsVal < 0) ||
                    (mode !== 'sip' && currValue < 1),
                  'text-brand-green-dark text-xs':
                    (mode === 'sip' && returnsVal >= 0) ||
                    (mode !== 'sip' && currValue >= 1),
                })}
              >
                {/* https://en.wikipedia.org/wiki/Floating-point_arithmetic#Accuracy_problems */}
                {returnsPercentage.toFixed(1)}%
              </span>
            </div>
          )}
        </div>
        {state.chartOptions ? (
          <div className="w-full">
            <DynamicChartWithNoSSR options={state.chartOptions} />
          </div>
        ) : null}
        {!!benchmarks?.length && (
          <TimePeriodSeries
            timePeriodsInMonths={benchmarks.map(item => item.label ?? '')}
            selected={state.duration}
            onTimePeriodSelected={async (month: string) => {
              await yieldToMain()
              void onTimePeriodSelected(month)
            }}
            className="!justify-center"
          />
        )}
        {etfComposition && (
          <div className="w-full">
            <IndexETFComposition
              peers={etfComposition}
              name={name}
              mode={mode}
              peer_rank={peer_rank}
              total_peers={total_peers}
            />
          </div>
        )}
      </div>
    </div>
  )
}
